import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ownerKey, useOwnerId } from "../features/auth/owner.ts";
import { api, query, type Schemas, type UploadProgress } from "./client.ts";

/**
 * La biblioteca escrita: los ESTUDIOS —citas de libros con su fuente, su tema y
 * tu nota— y las FRASES que rotan como frase del dia (migracion 0015).
 *
 * Son dos tablas y no una, y aqui se ven como dos hooks por la misma razon: una
 * cita tiene cinco campos y una frase tiene uno. Meterlas juntas obligaria a que
 * la mitad de los campos no aplicaran nunca, que es el problema que
 * `catalog_items` ya arrastra.
 *
 * LA ROTACION LA RESUELVE EL SERVIDOR, no esta pantalla: `?offset=` es el boton
 * "Otra frase". Asi es la misma frase a las siete de la manana y a las once de la
 * noche, y la misma en el telefono y en el portatil.
 */

export type Study = Schemas["StudyDto"];
export type StudyTopic = Schemas["StudyTopic"];
export type Phrase = Schemas["PhraseDto"];
export type PhraseOfTheDay = Schemas["PhraseOfTheDayDto"];
export type Material = Schemas["MaterialDto"];

/* El dueno detras del prefijo (ver `api/hooks.ts`): los estudios y las frases
   son del usuario, y un Mentor lee los de otro con `?owner=`. */
const keys = {
  studies: (owner: string | undefined, topic: StudyTopic | "") =>
    ["library", ownerKey(owner), "studies", topic] as const,
  phrases: (owner: string | undefined) => ["library", ownerKey(owner), "phrases"] as const,
  phraseToday: (owner: string | undefined, offset: number) =>
    ["library", ownerKey(owner), "phrase-today", offset] as const,
  material: (owner: string | undefined) => ["library", ownerKey(owner), "material"] as const,
};

/**
 * El TEMA lo filtra el servidor y no esta pantalla. Cuesta una peticion por tema
 * —cacheada, y son seis— y lo que compra es que el filtro siga diciendo la
 * verdad el dia que el listado se pagine: filtrar aqui filtraria solo lo que ya
 * se trajo.
 */
export function useStudies(topic: StudyTopic | "") {
  const owner = useOwnerId();

  return useQuery({
    queryKey: keys.studies(owner, topic),
    queryFn: () => api.get<Study[]>(`/api/v1/library/studies${query({ topic, owner })}`),
  });
}

export function usePhrases() {
  const owner = useOwnerId();

  return useQuery({
    queryKey: keys.phrases(owner),
    queryFn: () => api.get<Phrase[]>(`/api/v1/library/phrases${query({ owner })}`),
  });
}

/**
 * La frase de hoy. `offset` avanza en la rotacion; con la biblioteca vacia el
 * servidor devuelve 200 con todo nulo y `rotationSize: 0`, que no es un error
 * sino la respuesta correcta a "que frase toca cuando no hay ninguna".
 */
export function usePhraseOfTheDay(offset: number) {
  const owner = useOwnerId();

  return useQuery({
    queryKey: keys.phraseToday(owner, offset),
    queryFn: () =>
      api.get<PhraseOfTheDay>(
        `/api/v1/library/phrases/today${query({
          offset: offset === 0 ? undefined : offset,
          owner,
        })}`,
      ),
    staleTime: 5 * 60_000,
  });
}

/*
 * Las tres invalidaciones cuelgan del prefijo "library" a proposito: crear una
 * frase cambia el listado Y la frase de hoy —entra en la rotacion—, y marcar su
 * estrella cambia QUE frases rotan. Una sola invalidacion por prefijo alcanza a
 * las dos, y asi no hay forma de escribir una mutacion que se olvide de una.
 */
function useLibraryMutation<TInput, TResult>(run: (input: TInput) => Promise<TResult>) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: run,
    onSuccess: () => client.invalidateQueries({ queryKey: ["library"] }),
  });
}

export function useCreateStudy() {
  return useLibraryMutation((body: unknown) =>
    api.post<Schemas["CreatedResponse"]>("/api/v1/library/studies", body),
  );
}

export function useUpdateStudy(id: string) {
  return useLibraryMutation((body: unknown) => api.put<void>(`/api/v1/library/studies/${id}`, body));
}

export function useDeleteStudy() {
  return useLibraryMutation((id: string) => api.del<void>(`/api/v1/library/studies/${id}`));
}

export function useCreatePhrase() {
  return useLibraryMutation((body: unknown) =>
    api.post<Schemas["CreatedResponse"]>("/api/v1/library/phrases", body),
  );
}

export function useUpdatePhrase(id: string) {
  return useLibraryMutation((body: unknown) => api.put<void>(`/api/v1/library/phrases/${id}`, body));
}

/**
 * MARCAR UNA FRASE desde la pantalla de frases: su estrella o su emocion.
 *
 * Lleva el `id` EN LA LLAMADA y no en el hook, al contrario que
 * `useUpdatePhrase`. Es la diferencia entre una pantalla con un dialogo -- donde
 * se edita una frase cada vez -- y un carrusel de cuatrocientas: un hook por
 * tarjeta serian cuatrocientas mutaciones montadas para usar una.
 *
 * Manda la frase ENTERA, texto incluido, porque el PUT es completo y el `xmin`
 * decide: si otra pestana corrigio el texto mientras esta estaba abierta, esto
 * devuelve 409 en vez de reponer el texto viejo.
 */
export function useMarkPhrase() {
  return useLibraryMutation((input: { id: string; body: unknown }) =>
    api.put<void>(`/api/v1/library/phrases/${input.id}`, input.body),
  );
}

export function useDeletePhrase() {
  return useLibraryMutation((id: string) => api.del<void>(`/api/v1/library/phrases/${id}`));
}

/** Los seis temas del contrato, con su rotulo en espanol y en un solo sitio. */
export const STUDY_TOPICS = [
  { value: "psychology", label: "Psicologia" },
  { value: "risk", label: "Riesgo" },
  { value: "discipline", label: "Disciplina" },
  { value: "management", label: "Gestion" },
  { value: "strategy", label: "Estrategia" },
  { value: "other", label: "Otros" },
] as const satisfies readonly { value: StudyTopic; label: string }[];

export function labelTopic(value: StudyTopic): string {
  return STUDY_TOPICS.find((item) => item.value === value)?.label ?? value;
}


/**
 * SOCIAL DATA: el material de redes e internet (migracion 0023).
 *
 * Tabla propia y no una captura mas ni un estudio: una imagen de Twitter no
 * pertenece a ninguna operacion y no es una cita con procedencia. El propietario
 * lo pidio asi -- "no quiero que se mescle con mis capturas ni estudios".
 *
 * Sin paginacion, como los estudios: es una coleccion de decenas y leerla de un
 * tiron permite buscar dentro en el cliente sin una peticion por letra.
 */
export function useMaterial() {
  const owner = useOwnerId();

  return useQuery({
    queryKey: keys.material(owner),
    queryFn: () => api.get<Material[]>(`/api/v1/library/material${query({ owner })}`),
  });
}

/**
 * Subir UNA pieza, con progreso. Multipart, como las capturas de una operacion:
 * el archivo viaja tal cual y el almacen del servidor aplica las cuatro guardas
 * de SPEC 14.
 *
 * Devuelve una FUNCION y no una mutacion, por el mismo motivo que
 * `useUploadTradeImage`: `useMutation` guarda UN estado, y aqui se pegan cinco
 * imagenes de golpe con Ctrl+V, cada una con su barra y su error. Con una sola
 * mutacion la segunda pisaria el estado de la primera y la pantalla mentiria
 * sobre cual esta subiendo. El estado por archivo lo lleva la pantalla.
 *
 * El progreso no es un adorno: una captura de 6 MB desde el telefono en una
 * conexion de campo es una barra quieta cuarenta segundos, y eso se lee como "se
 * colgo" (ver `api/client.ts`).
 */
export function useUploadMaterial(): (input: {
  file: File;
  onProgress: (progress: UploadProgress) => void;
}) => Promise<Material> {
  const client = useQueryClient();

  return async (input) => {
    const form = new FormData();

    /* Solo el archivo. El titulo, la fuente y la nota se ponen DESPUES, con el
       lapiz de la pieza: el gesto es pegar algo que acabas de ver, y pedir tres
       campos en ese momento es la forma mas segura de que no se guarde nada
       (0023_material.sql). */
    form.append("file", input.file, input.file.name);

    const material = await api.upload<Material>(
      "/api/v1/library/material",
      form,
      input.onProgress,
    );

    await client.invalidateQueries({ queryKey: ["library"] });

    return material;
  };
}

export function useUpdateMaterial(id: string) {
  return useLibraryMutation((body: unknown) =>
    api.put<void>(`/api/v1/library/material/${id}`, body),
  );
}

export function useDeleteMaterial() {
  return useLibraryMutation((id: string) => api.del<void>(`/api/v1/library/material/${id}`));
}
